/**
 * @file pages/ProxyRoutes.jsx
 * @description Proxy route management page.
 *
 * Features:
 *  - Lists all configured external proxy routes (list layout, not a wide table,
 *    so it doesn't overflow horizontally on narrow screens) with path prefix,
 *    upstream URL, method, auth requirement, and active/inactive status.
 *  - Inline toggle for the active flag.
 *  - Add new route via an inline form.
 *  - Edit route (opens form pre-filled with existing values).
 *  - Delete route (admin only) with confirmation.
 *
 * Access: admin and operator roles only.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useToast } from '../context/ToastContext';
import {
  listProxyRoutes,
  createProxyRoute,
  updateProxyRoute,
  deleteProxyRoute,
} from '../api/proxyRoutes';

/** HTTP method options for the form selector. */
const METHOD_OPTIONS = ['*', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** Initial state for a blank create/edit form. */
const EMPTY_FORM = {
  name:          '',
  description:   '',
  path_prefix:   '/api/v1/',
  upstream_url:  '',
  method:        '*',
  strip_prefix:  true,
  auth_required: true,
  logging_enabled: true,
  action_logging_enabled: true,
  active:        true,
};

/**
 * Proxy Routes management page.
 */
export default function ProxyRoutes() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  /** Controls visibility of the add/edit form panel. */
  const [showForm, setShowForm]   = useState(false);
  /** When editing, holds the ID of the route being edited. Null when creating. */
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);

  const { data: routes = [], isLoading, error } = useQuery({
    queryKey: ['proxyRoutes'],
    queryFn:  listProxyRoutes,
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }) => updateProxyRoute(id, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proxyRoutes'] }),
    onError:   (err) => addToast(err.message, 'danger'),
  });

  // Toggle logging mutation
  const loggingMutation = useMutation({
    mutationFn: ({ id, logging_enabled }) => updateProxyRoute(id, { logging_enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proxyRoutes'] }),
    onError:   (err) => addToast(err.message, 'danger'),
  });

  // Toggle action logging mutation
  const actionLoggingMutation = useMutation({
    mutationFn: ({ id, action_logging_enabled }) => updateProxyRoute(id, { action_logging_enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proxyRoutes'] }),
    onError:   (err) => addToast(err.message, 'danger'),
  });

  // Create / Update mutation
  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editingId ? updateProxyRoute(editingId, payload) : createProxyRoute(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxyRoutes'] });
      addToast(editingId ? 'Route updated' : 'Route created', 'success');
      resetForm();
    },
    onError: (err) => addToast(err.message, 'danger'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteProxyRoute,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxyRoutes'] });
      addToast('Route deleted', 'success');
    },
    onError: (err) => addToast(err.message, 'danger'),
  });

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (route) => {
    setForm({
      name:          route.name,
      description:   route.description || '',
      path_prefix:   route.path_prefix,
      upstream_url:  route.upstream_url,
      method:        route.method,
      strip_prefix:  route.strip_prefix,
      auth_required: route.auth_required,
      logging_enabled: route.logging_enabled,
      action_logging_enabled: route.action_logging_enabled,
      active:        route.active,
    });
    setEditingId(route.id);
    setShowForm(true);
  };

  const handleDelete = (route) => {
    if (window.confirm(`Permanently delete route "${route.name}"?`)) {
      deleteMutation.mutate(route.id);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      name:          form.name,
      description:   form.description || undefined,
      path_prefix:   form.path_prefix,
      upstream_url:  form.upstream_url,
      method:        form.method,
      strip_prefix:  form.strip_prefix,
      auth_required: form.auth_required,
      logging_enabled: form.logging_enabled,
      action_logging_enabled: form.action_logging_enabled,
      active:        form.active,
    });
  };

  return (
    <div className="page-content">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 fw-bold mb-0" style={{ color: 'var(--color-primary)' }}>
          <i className="bi bi-diagram-3 me-2" aria-hidden="true" />
          Proxy Routes
        </h1>
        <button
          className="btn"
          style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
          onClick={() => { resetForm(); setShowForm((v) => !v); }}
        >
          <i className={`bi ${showForm && !editingId ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`} />
          {showForm && !editingId ? 'Cancel' : 'New Route'}
        </button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="card mb-4 border-0 shadow-sm">
          <div
            className="card-header fw-semibold"
            style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
          >
            {editingId ? 'Edit Route' : 'New Proxy Route'}
          </div>
          <div className="card-body">
            <form onSubmit={handleFormSubmit}>
              <div className="row g-3">
                {/* Name */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    placeholder="My API v2"
                    required
                  />
                </div>

                {/* Method */}
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Method</label>
                  <select className="form-select" name="method" value={form.method} onChange={handleFormChange}>
                    {METHOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                {/* Flags */}
                <div className="col-md-4 d-flex flex-column justify-content-end gap-1 pb-1">
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="strip_prefix" name="strip_prefix" checked={form.strip_prefix} onChange={handleFormChange} />
                    <label className="form-check-label small" htmlFor="strip_prefix">Strip prefix before forwarding</label>
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="auth_required" name="auth_required" checked={form.auth_required} onChange={handleFormChange} />
                    <label className="form-check-label small" htmlFor="auth_required">Require API token</label>
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="logging_enabled" name="logging_enabled" checked={form.logging_enabled} onChange={handleFormChange} />
                    <label className="form-check-label small" htmlFor="logging_enabled">Log requests &amp; responses</label>
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="action_logging_enabled" name="action_logging_enabled" checked={form.action_logging_enabled} onChange={handleFormChange} />
                    <label className="form-check-label small" htmlFor="action_logging_enabled">Log actions</label>
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="active" name="active" checked={form.active} onChange={handleFormChange} />
                    <label className="form-check-label small" htmlFor="active">Active</label>
                  </div>
                </div>

                {/* Path prefix */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    External path prefix <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    name="path_prefix"
                    value={form.path_prefix}
                    onChange={handleFormChange}
                    placeholder="/api/v1/myapi2"
                    required
                  />
                  <div className="form-text">
                    External callers use this path. Must start with <code>/api/</code>.
                  </div>
                </div>

                {/* Upstream URL */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Upstream URL <span className="text-danger">*</span>
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    name="upstream_url"
                    value={form.upstream_url}
                    onChange={handleFormChange}
                    placeholder="https://service.neas.no/api"
                    required
                  />
                  <div className="form-text">
                    Requests are forwarded here{form.strip_prefix ? ' (prefix stripped)' : ' (full path kept)'}.
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    placeholder="Optional description"
                  />
                </div>

                <div className="col-12 d-flex gap-2">
                  <button
                    type="submit"
                    className="btn"
                    style={{ backgroundColor: 'var(--color-accent-2)', color: '#000' }}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? 'Saving…' : editingId ? 'Update Route' : 'Create Route'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Route list */}
      <div className="card">
        <div className="card-body p-0">
          {error ? (
            <div className="alert alert-danger m-3">{error.message}</div>
          ) : (
            <ul className="list-group list-group-flush">
              {isLoading && (
                <li className="list-group-item text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm"
                    style={{ color: 'var(--color-primary)' }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </li>
              )}
              {!isLoading && routes.length === 0 && (
                <li className="list-group-item text-center text-muted py-4">
                  No proxy routes configured yet.
                </li>
              )}
              {routes.map((route) => (
                <li key={route.id} className="list-group-item">
                  {/* Header line: method, path, name, edit/delete actions */}
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-1">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="badge"
                        style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                      >
                        {route.method}
                      </span>
                      <code className="small text-break">{route.path_prefix}</code>
                      <strong>{route.name}</strong>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(route)}>
                        <i className="bi bi-pencil" aria-hidden="true" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(route)}
                        disabled={deleteMutation.isPending}
                      >
                        <i className="bi bi-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {route.description && (
                    <div className="text-muted small mb-1">{route.description}</div>
                  )}

                  {/* Upstream URL — wraps instead of being truncated to a fixed column width */}
                  <div className="text-muted small text-break mb-2">
                    <i className="bi bi-arrow-right-short" aria-hidden="true" />
                    {route.upstream_url}
                  </div>

                  {/* Flags line: auth, strip-prefix, logging toggle, active toggle */}
                  <div className="d-flex flex-wrap align-items-center gap-3 small">
                    {route.auth_required
                      ? <span className="badge" style={{ backgroundColor: 'var(--color-accent-2)', color: '#000' }}>Auth Required</span>
                      : <span className="badge bg-secondary">Auth Open</span>}

                    <span className="text-muted">
                      <i className={`bi ${route.strip_prefix ? 'bi-check2 text-success' : 'bi-dash text-muted'} me-1`} />
                      Strip prefix
                    </span>

                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={route.logging_enabled}
                        onChange={() => loggingMutation.mutate({ id: route.id, logging_enabled: !route.logging_enabled })}
                        disabled={loggingMutation.isPending}
                        id={`logging-${route.id}`}
                      />
                      <label className="form-check-label text-muted" htmlFor={`logging-${route.id}`}>Logging</label>
                    </div>

                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={route.action_logging_enabled}
                        onChange={() => actionLoggingMutation.mutate({ id: route.id, action_logging_enabled: !route.action_logging_enabled })}
                        disabled={actionLoggingMutation.isPending}
                        id={`action-logging-${route.id}`}
                      />
                      <label className="form-check-label text-muted" htmlFor={`action-logging-${route.id}`}>Action logs</label>
                    </div>

                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={route.active}
                        onChange={() => toggleMutation.mutate({ id: route.id, active: !route.active })}
                        disabled={toggleMutation.isPending}
                        id={`active-${route.id}`}
                      />
                      <label className="form-check-label text-muted" htmlFor={`active-${route.id}`}>Active</label>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Path matching explanation */}
      <div className="alert alert-light border mt-3 small">
        <i className="bi bi-info-circle me-2 text-muted" />
        <strong>Token path matching:</strong> A token with <code>/api/v1/myapi2</code> in its
        <em> allowed_paths</em> grants access to that exact path AND all sub-paths
        (e.g. <code>/api/v1/myapi2/users</code>). Use <code>*</code> for unrestricted access.
      </div>
    </div>
  );
}
